import { useState, useCallback } from 'react'
import { ChatMessage } from '@/components/chat/ChatInterface'
import { AnchorMCPClient, createMCPClient } from '@/lib/mcp-client'
import { useToast } from './use-toast'
import { api } from '@/lib/api'

export interface PlaywrightScriptWorkflowState {
  messages: ChatMessage[]
  isLoading: boolean
  mcpClient: AnchorMCPClient | null
  isConnected: boolean
  currentSession: {
    sessionId: string | null
    liveUrl: string | null
    status: string
  }
  currentScript: {
    code: string
    description: string
  } | null
}

export function usePlaywrightScriptWorkflow() {
  const [state, setState] = useState<PlaywrightScriptWorkflowState>({
    messages: [],
    isLoading: false,
    mcpClient: null,
    isConnected: false,
    currentSession: {
      sessionId: null,
      liveUrl: null,
      status: 'idle'
    },
    currentScript: null
  })

  const { toast } = useToast()

  // Add message to chat
  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    setState(prev => ({
      ...prev,
      messages: [
        ...prev.messages,
        {
          ...message,
          id: Math.random().toString(36).substring(7),
          timestamp: new Date()
        }
      ]
    }))
  }, [])

  // Initialize MCP connection
  const initializeMCP = useCallback(async () => {
    try {
      const mcpClient = await createMCPClient()
      setState(prev => ({
        ...prev,
        mcpClient,
        isConnected: true
      }))
      
      // Add welcome message
      addMessage({
        role: 'assistant',
        content: 'Connected to Playwright MCP! I can help you generate Playwright automation scripts. Just describe what you want to automate.',
        type: 'text'
      })
    } catch (error) {
      console.error('Failed to initialize MCP:', error)
      throw error
    }
  }, [addMessage])

  // Send user message and get AI response
  const sendMessage = useCallback(async (content: string) => {
    if (!state.mcpClient || !state.isConnected) {
      toast({
        title: "Not Connected",
        description: "Please connect to MCP service first",
        variant: "destructive"
      })
      return
    }

    // Add user message
    addMessage({
      role: 'user',
      content,
      type: 'text'
    })

    setState(prev => ({ ...prev, isLoading: true }))

    try {
      // Generate Playwright code using MCP
      const codeGeneration = await state.mcpClient.generatePlaywrightCode(content)
      
      // Store the current script
      setState(prev => ({
        ...prev,
        currentScript: {
          code: codeGeneration.code,
          description: codeGeneration.description
        }
      }))
      
      // Add assistant response with generated code
      addMessage({
        role: 'assistant',
        content: codeGeneration.description,
        type: 'code',
        metadata: {
          playwrightCode: codeGeneration.code
        }
      })
    } catch (error) {
      console.error('Failed to generate code:', error)
      addMessage({
        role: 'assistant',
        content: 'Sorry, I encountered an error while generating the Playwright code. Please try again.',
        type: 'text'
      })
      toast({
        title: "Generation Failed",
        description: "Failed to generate Playwright code",
        variant: "destructive"
      })
    } finally {
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }, [state.mcpClient, state.isConnected, addMessage, toast])

  // Execute generated Playwright code via Stagehand with Browserbase
  const executeCode = useCallback(async (code: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }))

      // Create a Browserbase session for live viewing
      const sessionResponse = await api.request('/sessions/browserbase', {
        method: 'POST',
        body: JSON.stringify({
          viewport: { width: 1280, height: 720 }
        })
      }) as any

      // Update state with new Browserbase session
      setState(prev => ({
        ...prev,
        currentSession: {
          sessionId: sessionResponse.sessionId,
          liveUrl: sessionResponse.liveViewUrl,
          status: 'active'
        }
      }))

      // Execute the Playwright code via Stagehand using the simple approach
      const executionResponse = await api.request('/playwright-scripts/execute-stagehand', {
        method: 'POST',
        body: JSON.stringify({
          code: code,
          sessionId: sessionResponse.sessionId
        })
      }) as any

      // Add execution result message
      addMessage({
        role: 'assistant',
        content: `Script executed via Stagehand! ${executionResponse.status === 'success' ? 'Check the live browser session below.' : 'Execution failed: ' + (executionResponse.error_message || executionResponse.error || executionResponse.details || 'Unknown error')}`,
        type: 'execution',
        metadata: {
          executionResult: executionResponse,
          sessionId: sessionResponse.sessionId
        }
      })

    } catch (error) {
      console.error('Failed to execute code:', error)
      addMessage({
        role: 'assistant',
        content: 'Failed to execute the Playwright code. Please try again.',
        type: 'text'
      })
      toast({
        title: "Execution Failed",
        description: "Failed to execute Playwright code",
        variant: "destructive"
      })
    } finally {
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }, [addMessage, toast])

  // Execute code via CDP (alternative method)
  const executeCodeViaCDP = useCallback(async (code: string, sessionId?: string) => {
    console.log('executeCodeViaCDP called with:', { code: code.substring(0, 100) + '...', sessionId })
    try {
      setState(prev => ({ ...prev, isLoading: true }))

      // Create a Browserbase session for live viewing
      let session = sessionId ? { id: sessionId } : null

      if (!session) {
        console.log('Creating new Browserbase session...')
        // Create Browserbase session via our API
        const sessionResponse = await api.request('/sessions/browserbase', {
          method: 'POST',
          body: JSON.stringify({
            viewport: { width: 1280, height: 720 }
          })
        }) as any

        console.log('Session response:', sessionResponse)

        if (!sessionResponse.sessionId || !sessionResponse.liveViewUrl) {
          throw new Error(`Invalid session response: ${JSON.stringify(sessionResponse)}`)
        }

        session = { id: sessionResponse.sessionId }

        console.log('Updating state with session:', {
          sessionId: sessionResponse.sessionId,
          liveUrl: sessionResponse.liveViewUrl,
          status: 'active'
        })

        setState(prev => ({
          ...prev,
          currentSession: {
            sessionId: sessionResponse.sessionId,
            liveUrl: sessionResponse.liveViewUrl,
            status: 'active'
          }
        }))

        console.log('State updated successfully')
      }

      // Execute via Stagehand (AI-native automation with Browserbase)
      const result = await api.request('/playwright-scripts/execute', {
        method: 'POST',
        body: JSON.stringify({
          script_id: 'temp',
          parameters: {},
          execution_method: 'stagehand',
          code: code,
          session_id: session?.id // Optional - Browserbase will create if not provided
        })
      }) as any

      console.log('🔍 Execution result:', result);
      console.log('🔍 Current session before update:', state.currentSession);

      // Update session state with Browserbase session info, but preserve the correct live view URL
      if (result.status === 'success' && result.browser_session_id) {
        console.log('✅ Updating session with execution result');
        setState(prev => ({
          ...prev,
          currentSession: {
            sessionId: result.browser_session_id,
            // Keep the correct live view URL from session creation, don't override with API response
            liveUrl: prev.currentSession.liveUrl || result.live_view_url || `https://www.browserbase.com/sessions/${result.browser_session_id}`,
            status: 'active'
          }
        }))
      } else {
        console.log('⚠️ Execution result missing browser_session_id, preserving existing session');
        console.log('⚠️ Result status:', result.status);
        console.log('⚠️ Result browser_session_id:', result.browser_session_id);
        // Preserve the existing session since execution result doesn't have session info
        // Don't reset the session state
      }

      addMessage({
        role: 'assistant',
        content: `Code executed via Stagehand! ${result.status === 'success' ? 'Check the live browser session below.' : 'Execution failed: ' + (result.error_message || result.error || result.details || 'Unknown error')}`,
        type: 'execution',
        metadata: {
          executionResult: result,
          sessionId: result.browser_session_id || session?.id,
          executionMethod: 'stagehand'
        }
      })

    } catch (error) {
      console.error('Failed to execute code via CDP:', error)
      console.error('Error details:', error instanceof Error ? error.message : String(error))
      addMessage({
        role: 'assistant',
        content: `Failed to execute code via CDP: ${error instanceof Error ? error.message : String(error)}`,
        type: 'text'
      })
    } finally {
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }, [addMessage])

  // Save current script to library
  const saveScript = useCallback(async (name: string, description?: string) => {
    if (!state.currentScript) {
      throw new Error('No script to save')
    }

    try {
      const response = await api.request('/playwright-scripts', {
        method: 'POST',
        body: JSON.stringify({
          name,
          description: description || state.currentScript.description,
          code: state.currentScript.code,
          metadata: {
            complexity: 'medium',
            tags: ['generated', 'mcp'],
            estimated_runtime: '30s'
          }
        })
      })

      addMessage({
        role: 'assistant',
        content: `Script "${name}" has been saved to your library! You can find it in your Playwright Scripts collection.`,
        type: 'text'
      })

      return response
    } catch (error) {
      console.error('Failed to save script:', error)
      throw error
    }
  }, [state.currentScript, addMessage])

  // Clear messages
  const clearMessages = useCallback(() => {
    setState(prev => ({ ...prev, messages: [] }))
  }, [])

  return {
    messages: state.messages,
    isLoading: state.isLoading,
    isConnected: state.isConnected,
    currentSession: state.currentSession,
    currentScript: state.currentScript,
    sendMessage,
    executeCode,
    executeCodeViaCDP,
    saveScript,
    clearMessages,
    initializeMCP
  }
}
